<template>
  <b-card :title="isEdit ? 'Editar Conta' : 'Nova Conta'">
    <b-form @submit.prevent="save">
      <b-row>
        <!-- Row 1 -->
        <b-col md="6">
          <b-form-group label="Nome da Conta *">
            <b-form-input
              v-model="form.descricao"
              required
              placeholder="Ex: Caixa Interno, Conta Itaú Principal..."
            />
          </b-form-group>
        </b-col>

        <b-col md="6">
          <b-form-group label="Tipo de Conta *">
            <b-form-select
              v-model="form.tipoConta"
              :options="tiposContaOptions"
              value-field="id"
              text-field="nome"
              required
            >
              <template #first>
                <b-form-select-option :value="null" disabled>Selecione um tipo...</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
        </b-col>

        <!-- Row 2 -->
        <b-col md="6">
          <b-form-group label="Banco (Opcional se for Caixa)">
            <v-select
              v-model="form.banco"
              :options="bancosOptions"
              label="nome"
              placeholder="Selecione o banco"
            />
          </b-form-group>
        </b-col>
        
        <b-col md="2">
          <b-form-group label="Agência">
            <b-form-input
              v-model="form.agencia"
              placeholder="0000"
            />
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group label="Conta">
            <b-form-input
              v-model="form.conta"
              placeholder="00000"
            />
          </b-form-group>
        </b-col>

        <b-col md="1">
          <b-form-group label="Dígito">
            <b-form-input
              v-model="form.digitoVerificador"
              placeholder="0"
            />
          </b-form-group>
        </b-col>

        <!-- Row 3 -->
        <b-col md="3">
          <b-form-group label="Saldo Inicial (R$)">
            <money
              v-model="form.valorSaldoInicial"
              v-bind="money"
              class="form-control text-right"
              :disabled="isEdit"
            />
            <small v-if="isEdit" class="text-muted">Apenas na criação.</small>
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group label="Data Saldo Inicial">
            <flat-pickr
              v-model="form.dataSaldoInicial"
              class="form-control"
              :config="configDate"
              placeholder="Selecione uma data"
              :disabled="isEdit"
            />
          </b-form-group>
        </b-col>

        <b-col md="3">
          <b-form-group label="Moeda">
            <b-form-select
              v-model="form.moeda"
              :options="moedasOptions"
            />
          </b-form-group>
        </b-col>
        
        <!-- Booleans -->
        <b-col md="3" class="mt-2">
          <div class="d-flex flex-column gap-1">
            <b-form-checkbox v-model="form.principal" switch class="mb-50">
              Conta Principal
            </b-form-checkbox>
            <b-form-checkbox v-model="form.permiteSaldoNegativo" switch class="mb-50">
              Permite Saldo Negativo
            </b-form-checkbox>
            <b-form-checkbox v-model="form.ativo" switch>
              Conta Ativa
            </b-form-checkbox>
          </div>
        </b-col>

        <!-- Descrição removida pois 'descricao' é o Nome da conta no banco de dados -->
      </b-row>

      <hr />
      
      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-1" @click="cancel">
          Cancelar
        </b-button>
        <b-button type="submit" variant="primary">
          <feather-icon icon="SaveIcon" class="mr-50" />
          {{ isEdit ? 'Salvar Alterações' : 'Cadastrar Conta' }}
        </b-button>
      </div>
    </b-form>
  </b-card>
</template>

<script>
import {
  BRow, BCol, BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormSelectOption, BFormCheckbox, BFormTextarea, BButton
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { Money } from 'v-money'
import flatPickr from 'vue-flatpickr-component'
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import { mapState } from 'vuex'
import mixinsGlobal from '@/mixins'

export default {
  name: 'ContaForm',
  components: {
    BRow, BCol, BCard, BForm, BFormGroup, BFormInput, BFormSelect, BFormSelectOption, BFormCheckbox, BFormTextarea, BButton,
    vSelect,
    Money,
    flatPickr
  },
  mixins: [mixinsGlobal],
  data() {
    return {
      isEdit: false,
      form: {
        id: '',
        descricao: '',
        tipoConta: null,
        banco: null,
        agencia: '',
        conta: '',
        digitoVerificador: '',
        dataSaldoInicial: null,
        valorSaldoInicial: 0.0,
        moeda: 'BRL',
        principal: false,
        permiteSaldoNegativo: false,
        ativo: true
      },
      moedasOptions: [
        { value: 'BRL', text: 'Real (BRL)' },
        { value: 'USD', text: 'Dólar Americano (USD)' },
        { value: 'EUR', text: 'Euro (EUR)' }
      ],
      configDate: {
        dateFormat: 'Y-m-d',
        locale: Portuguese
      },
      money: {
        decimal: ',',
        thousands: '.',
        prefix: '',
        suffix: '',
        precision: 2,
        masked: false
      }
    }
  },
  computed: {
    ...mapState('contaFinancialModule', ['conta']),
    ...mapState('combosModule', {
      bancosOptions: state => state.comboBancos,
      tiposContaOptions: state => state.comboTipoContas
    })
  },
  async mounted() {
    await this.loadCombos()
    if (this.$route.params.id || this.conta.id) {
      this.isEdit = true
      this.loadContaForEdit()
    } else {
      this.$store.dispatch('contaFinancialModule/reset')
      this.form = Object.assign({}, this.$store.state.contaFinancialModule.conta)
    }
  },
  methods: {
    async loadCombos() {
      await Promise.all([
        this.$store.dispatch('combosModule/loadComboBancos'),
        this.$store.dispatch('combosModule/loadComboTipoConta')
      ])
    },
    async loadContaForEdit() {
      const id = this.$route.params.id || this.conta.id
      try {
        const response = await this.$store.dispatch('contaFinancialModule/findById', id)
        if (response && response.data) {
          const dt = response.data
          this.form = {
            id: dt.id,
            descricao: dt.descricao,
            tipoConta: dt.tipoConta,
            banco: dt.banco,
            agencia: dt.agencia,
            conta: dt.conta,
            digitoVerificador: dt.digitoVerificador,
            dataSaldoInicial: dt.dataSaldoInicial,
            valorSaldoInicial: dt.valorSaldoInicial || 0.0,
            moeda: dt.moeda || 'BRL',
            principal: dt.principal,
            permiteSaldoNegativo: dt.permiteSaldoNegativo,
            ativo: dt.ativo
          }
        }
      } catch (e) {
        this.MensagemError('Erro', 'Não foi possível carregar a Conta.')
        this.cancel()
      }
    },
    async save() {
      // Validate
      if (!this.form.descricao) {
        this.MensagemError('Validação', 'O nome da conta é obrigatório.')
        return
      }

      const payload = { ...this.form }
      
      let action = 'contaFinancialModule/insert'
      if (this.isEdit) {
        action = 'contaFinancialModule/update'
      }

      try {
        const response = await this.$store.dispatch(action, payload)
        if (response && response.data && response.data.id) {
          this.showMessageSuccess('Sucesso', `Conta ${this.isEdit ? 'atualizada' : 'cadastrada'} com sucesso!`)
          this.$router.push({ name: 'contas' })
        } else if (response.response && response.response.data && response.response.data.errors) {
          this.MensagemError('Erro', response.response.data.errors[0].message)
        } else {
          this.MensagemError('Erro', `Falha ao ${this.isEdit ? 'atualizada' : 'cadastrada'} a conta.`)
        }
      } catch (error) {
        this.MensagemError('Erro', 'Ocorreu um erro na requisição.')
      }
    },
    cancel() {
      this.$router.push({ name: 'contas' })
    }
  }
}
</script>

<style scoped>
.gap-1 { gap: 0.25rem; }
</style>
